import React, { Suspense, useCallback, useEffect, useRef, useState } from "react";
import { Canvas, useLoader, useFrame, useThree } from "@react-three/fiber";
import { useProgress, Html, Environment } from "@react-three/drei";
import { useRotatedHDRI } from "../useRotatedHDRI";
import { GLTFLoader } from "three/examples/jsm/loaders/GLTFLoader";
import { DRACOLoader } from "three/examples/jsm/loaders/DRACOLoader";
import * as THREE from "three";
import { MODEL_URL, HDRI_URL } from "../assets";
import "../styles/DesktopRenderer.css";

const dracoLoader = new DRACOLoader();
dracoLoader.setDecoderPath("/draco/");

// Siła oświetlenia/odbić z HDRI - 0.6 = 60% pełnej mocy environment mapy
const HDRI_INTENSITY = 0.25;

// Komponent ładowania - pełnoekranowy, wyśrodkowany, w złotej stylistyce
// strony startowej (zawsze w całości widoczny, niezależnie od rozmiaru okna)
function LoadingIndicator() {
  const { progress } = useProgress();
  const displayProgress = Math.min(Math.round(progress), 100);

  return (
    <Html fullscreen>
      <div className="scene-loader">
        <div className="scene-loader-ring">
          <span className="scene-loader-percent">{displayProgress}%</span>
        </div>
        <div className="scene-loader-label">Ładowanie wnętrza</div>
        <div className="scene-loader-track">
          <div
            className="scene-loader-fill"
            style={{ width: `${displayProgress}%` }}
          />
        </div>
      </div>
    </Html>
  );
}

// Komponent modelu 3D - wykrywa ściany na potrzeby kolizji i obrazy do oglądania
const Model = ({ setWalls, setArtworks }) => {
  const gltf = useLoader(GLTFLoader, MODEL_URL, (loader) => {
    loader.setDRACOLoader(dracoLoader);
  });
  const modelRef = useRef();
  const walls = useRef([]);
  const artworks = useRef([]);
  const [lightsConfigured, setLightsConfigured] = useState(false);
  const originalIntensities = useRef(new Map());

  useEffect(() => {
    if (gltf && !lightsConfigured) {
      // KLUCZOWE: efekt może odpalić się zanim renderer skomponuje macierze
      // świata nowo dodanej sceny - bez tego boundingBox.applyMatrix4(matrixWorld)
      // liczy pozycje z macierzy jednostkowej (obiekty "lądują" przy podłodze
      // w lokalnych współrzędnych geometrii).
      gltf.scene.updateMatrixWorld(true);

      const wallObjects = [];
      // GLTFLoader dzieli mesh z wieloma primitives na kilka obiektów-dzieci
      // (np. "paintings_1/2/3" dla ramki/sznurka/passe-partout jednego obrazu),
      // więc grupujemy je po nazwie bazowej, żeby jeden obraz = jeden cel kliknięcia.
      const artworkGroups = new Map();

      // Najpierw znajdź obiekt 'walls' (rodzic)
      let wallsParent = null;
      gltf.scene.traverse((node) => {
        if (node.name && node.name.toLowerCase() === "walls") {
          wallsParent = node;
          console.log("Znaleziono obiekt-rodzic 'walls':", wallsParent);
        }
      });

      // Przeszukaj model w poszukiwaniu ścian i skonfiguruj cienie
      gltf.scene.traverse((node) => {
        if (node.isMesh) {
          node.castShadow = true;
          node.receiveShadow = true;

          if (Array.isArray(node.material)) {
            node.material.forEach((m) => {
              m.envMapIntensity = HDRI_INTENSITY;
            });
          } else if (node.material) {
            node.material.envMapIntensity = HDRI_INTENSITY;
          }

          // Identyfikuj obrazy do oglądania (painting1..paintingN; GLTFLoader
          // rozbija mesh wieloma materiałami na dzieci z sufiksem "_1", "_2"...)
          if (/^painting/i.test(node.name)) {
            const baseName = node.name.replace(/_\d+$/, "");

            // Sklonuj materiały TYLKO dla obrazów: "gold"/"gold_soft" są
            // współdzielone z listwami/sztukaterią całej sceny, więc ustawienie
            // emissive na wspólnej instancji podświetlałoby złocenia w całym
            // pokoju, a animacje 4 obrazów nadpisywałyby się nawzajem.
            if (Array.isArray(node.material)) {
              node.material = node.material.map((m) => m.clone());
            } else if (node.material) {
              node.material = node.material.clone();
            }

            node.geometry.computeVertexNormals();
            node.geometry.computeBoundingBox();
            const meshBox = node.geometry.boundingBox
              .clone()
              .applyMatrix4(node.matrixWorld);

            if (!artworkGroups.has(baseName)) {
              artworkGroups.set(baseName, {
                meshes: [],
                box: new THREE.Box3(),
              });
            }
            const group = artworkGroups.get(baseName);
            group.meshes.push(node);

            // Sznurek ("string") jest klikalny, ale nie wliczamy go do kadru -
            // ciągnie się w górę do haka i zawyżałby środek oraz wysokość obrazu.
            const materialName =
              (node.material && node.material.name) || "";
            if (materialName !== "string") {
              group.box.union(meshBox);
            }
          }

          // Identyfikuj ściany - sprawdź czy jest dzieckiem obiektu 'walls'
          // LUB ma w nazwie słowo wall/ściana/itp.
          if (
            (wallsParent &&
              node.parent &&
              (node.parent === wallsParent ||
                node.parent.parent === wallsParent)) ||
            /wall/i.test(node.name) ||
            node.name.toLowerCase().includes("sciana") ||
            node.name.toLowerCase().includes("ściana") ||
            node.name.toLowerCase().includes("col") ||
            node.name.toLowerCase().includes("floor") ||
            node.name.toLowerCase().includes("ceiling") ||
            node.name.toLowerCase().includes("sufit") ||
            node.name.toLowerCase().includes("podloga") ||
            node.name.toLowerCase().includes("podłoga")
          ) {
            console.log(
              "Dodaję ścianę do kolizji:",
              node.name,
              "rodzic:",
              node.parent ? node.parent.name : "brak",
            );
            node.geometry.computeBoundingBox();
            const box = node.geometry.boundingBox.clone();
            box.applyMatrix4(node.matrixWorld);

            wallObjects.push({
              name: node.name,
              box: box,
              position: node.position.clone(),
              quaternion: node.quaternion.clone(),
              scale: node.scale.clone(),
              mesh: node,
            });
          }
        }

        if (node.isLight) {
          if (!originalIntensities.current.has(node.uuid)) {
            originalIntensities.current.set(node.uuid, node.intensity);
          }

          const originalIntensity = originalIntensities.current.get(node.uuid);
          node.intensity = originalIntensity * 0.005;
        }
      });

      // Zbuduj finalną listę obrazów z pogrupowanych meshy
      const artworkObjects = [];
      artworkGroups.forEach((group, baseName) => {
        const center = new THREE.Vector3();
        group.box.getCenter(center);

        const size = new THREE.Vector3();
        group.box.getSize(size);

        // Obraz wiszący na ścianie jest najcieńszy w osi swojej normalnej -
        // bierzemy poziomą oś (X lub Z), w której box jest cieńszy.
        // To daje kierunek "od przodu" niezależnie od konwencji osi w modelu.
        const faceNormal =
          size.x < size.z
            ? new THREE.Vector3(1, 0, 0)
            : new THREE.Vector3(0, 0, 1);

        // Zwrot: w stronę pomieszczenia (od środka pokoju), nie w ścianę.
        const towardRoomCenter = new THREE.Vector3(
          -center.x,
          0,
          -center.z,
        ).normalize();
        if (faceNormal.dot(towardRoomCenter) < 0) {
          faceNormal.negate();
        }

        artworkObjects.push({
          name: baseName,
          meshes: group.meshes,
          position: center.clone(),
          normal: faceNormal.clone(),
          size: size.clone(),
        });
      });

      // Stała kolejność przełączania klawiszami A/D: painting1, painting2...
      artworkObjects.sort((a, b) =>
        a.name.localeCompare(b.name, undefined, { numeric: true }),
      );

      walls.current = wallObjects;
      artworks.current = artworkObjects;

      // Przekaż informacje o ścianach i obrazach do komponentu nadrzędnego
      if (setWalls) setWalls(wallObjects);
      if (setArtworks) setArtworks(artworkObjects);

      console.log("Wykryto ścian:", wallObjects.length);
      console.log("Wykryto obrazów:", artworkObjects.length);

      // Oznacz, że światła zostały już skonfigurowane
      setLightsConfigured(true);
    }
  }, [gltf, setWalls, setArtworks, lightsConfigured]);

  return <primitive object={gltf.scene} ref={modelRef} />;
};

// Funkcja easing dla płynniejszych animacji przejścia kamery
function easeInOutCubic(t) {
  return t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2;
}

// Obsługa oglądania obrazów - kamera podjeżdża i obraca się w stronę obrazu,
// zapamiętując pozycję startową, do której płynnie wraca po wyjściu.
function ArtworkInteraction({
  artworks,
  setViewingArtwork,
  viewingArtwork,
  isLookingAround,
}) {
  const { raycaster, pointer, gl, camera } = useThree();

  const [isTransitioning, setIsTransitioning] = useState(false);
  // Pozycja/rotacja z której zaczęła się CAŁA sesja oglądania (ustawiana raz,
  // niezmienna przy przełączaniu między obrazami) - tu wracamy po wyjściu.
  const roomStartPosition = useRef(null);
  const roomStartQuaternion = useRef(null);
  // Punkt startowy AKTUALNEJ animacji (dojazd do obrazu lub powrót) - zawsze
  // aktualna pozycja kamery w momencie rozpoczęcia przejścia.
  const transitionFromPosition = useRef(null);
  const transitionFromQuaternion = useRef(null);
  const transitionStartTime = useRef(0);
  const transitionDuration = 1000; // ms
  const targetPosition = useRef(null);
  const targetQuaternion = useRef(null);

  const [hoveredArtwork, setHoveredArtwork] = useState(null);

  const flyToArtwork = useCallback((artwork) => {
    // Zapamiętaj pozycję startową tylko przy WEJŚCIU do trybu oglądania -
    // przełączanie między obrazami (A/D) nie może jej nadpisać.
    if (!roomStartPosition.current) {
      roomStartPosition.current = camera.position.clone();
      roomStartQuaternion.current = camera.quaternion.clone();
    }

    transitionFromPosition.current = camera.position.clone();
    transitionFromQuaternion.current = camera.quaternion.clone();

    const center = artwork.position.clone();
    const size = artwork.size.clone();

    // Kadrowanie z FOV kamery: dobierz dystans tak, by obraz (szerokość
    // w poziomie prostopadle do normalnej + wysokość) mieścił się w kadrze
    // z lekkim marginesem.
    const width = Math.abs(artwork.normal.x) > 0.5 ? size.z : size.x;
    const height = size.y;
    const vFov = THREE.MathUtils.degToRad(camera.fov);
    const distForHeight = (height / 2) / Math.tan(vFov / 2);
    const hFov = 2 * Math.atan(Math.tan(vFov / 2) * camera.aspect);
    const distForWidth = (width / 2) / Math.tan(hFov / 2);
    const viewDistance = Math.max(distForHeight, distForWidth) * 1.15;
    const adjustedDistance = Math.max(1.0, Math.min(viewDistance, 4.0));

    const cameraPosition = center
      .clone()
      .add(artwork.normal.clone().multiplyScalar(adjustedDistance));

    cameraPosition.y = Math.max(center.y, 1.5);

    targetPosition.current = cameraPosition;

    // Uwaga: setFromUnitVectors(( 0,0,-1), direction) liczy NAJKRÓTSZY obrót
    // między wektorami, co przy obrazach powyżej wysokości oczu wprowadza
    // niekontrolowany "roll" (przechylenie kamery) - stąd wcześniejsze
    // "wygięcie" toru kamery. lookAt z wektorem "up" gwarantuje brak przechyłu.
    const lookAtMatrix = new THREE.Matrix4().lookAt(
      cameraPosition,
      center,
      new THREE.Vector3(0, 1, 0),
    );
    targetQuaternion.current = new THREE.Quaternion().setFromRotationMatrix(
      lookAtMatrix,
    );

    setIsTransitioning(true);
    transitionStartTime.current = Date.now();
    setViewingArtwork(artwork);
  }, [camera, setViewingArtwork]);

  const exitArtwork = useCallback(() => {
    if (!roomStartPosition.current) {
      setViewingArtwork(null);
      return;
    }

    transitionFromPosition.current = camera.position.clone();
    transitionFromQuaternion.current = camera.quaternion.clone();
    targetPosition.current = roomStartPosition.current;
    targetQuaternion.current = roomStartQuaternion.current;

    setIsTransitioning(true);
    transitionStartTime.current = Date.now();

    setTimeout(() => {
      setViewingArtwork(null);
      roomStartPosition.current = null;
      roomStartQuaternion.current = null;
    }, transitionDuration);
  }, [camera, setViewingArtwork]);

  // Kliknięcie na obraz + podświetlenie po najechaniu
  useEffect(() => {
    const findArtworkByMeshId = (meshId) =>
      artworks.find((artwork) =>
        artwork.meshes.some((m) => m.id === meshId),
      );

    const handleClick = (event) => {
      if (event.button === 2) return;
      if (isTransitioning) return;

      raycaster.setFromCamera(pointer, camera);
      const artworkMeshes = artworks.flatMap((artwork) => artwork.meshes);
      const intersects = raycaster.intersectObjects(artworkMeshes);

      if (intersects.length > 0) {
        const clickedArtwork = findArtworkByMeshId(intersects[0].object.id);
        if (clickedArtwork) flyToArtwork(clickedArtwork);
      }
    };

    const handleMouseMove = () => {
      raycaster.setFromCamera(pointer, camera);
      const artworkMeshes = artworks.flatMap((artwork) => artwork.meshes);
      const intersects = raycaster.intersectObjects(artworkMeshes);

      if (isTransitioning || isLookingAround) {
        if (hoveredArtwork !== null) {
          setHoveredArtwork(null);
          gl.domElement.style.cursor = "auto";
        }
        return;
      }

      if (intersects.length > 0) {
        const hoveredEntry = findArtworkByMeshId(intersects[0].object.id);
        if (hoveredEntry && (!viewingArtwork || viewingArtwork.name !== hoveredEntry.name)) {
          setHoveredArtwork(hoveredEntry.name);
          gl.domElement.style.cursor = "pointer";
        } else {
          setHoveredArtwork(null);
          gl.domElement.style.cursor = "auto";
        }
      } else {
        setHoveredArtwork(null);
        gl.domElement.style.cursor = "auto";
      }
    };

    gl.domElement.addEventListener("mousedown", handleClick);
    gl.domElement.addEventListener("mousemove", handleMouseMove);

    return () => {
      gl.domElement.removeEventListener("mousedown", handleClick);
      gl.domElement.removeEventListener("mousemove", handleMouseMove);
    };
  }, [
    raycaster,
    pointer,
    camera,
    artworks,
    viewingArtwork,
    isTransitioning,
    gl,
    isLookingAround,
    hoveredArtwork,
    setViewingArtwork,
    flyToArtwork,
  ]);

  // Podświetlenie (emissive) obrazu pod kursorem - działa na wszystkich
  // meshach danego obrazu (ramka/sznurek/passe-partout) na raz.
  useEffect(() => {
    const animations = new Map();

    artworks.forEach((artwork) => {
      const shouldHighlight =
        artwork.name === hoveredArtwork &&
        !isLookingAround &&
        (!viewingArtwork || viewingArtwork.name !== artwork.name);

      artwork.meshes.forEach((mesh) => {
        if (!mesh.material || !mesh.material.emissive) return;

        if (!mesh.userData.originalEmissive) {
          mesh.userData.originalEmissive = mesh.material.emissive.clone();
        }

        const targetColor = shouldHighlight
          ? new THREE.Color(0.2, 0.2, 0.2)
          : mesh.userData.originalEmissive.clone();

        if (animations.has(mesh.id)) {
          cancelAnimationFrame(animations.get(mesh.id));
          animations.delete(mesh.id);
        }

        const startTime = Date.now();
        const duration = 300;
        const startColor = mesh.material.emissive.clone();

        const animateHighlight = () => {
          const elapsed = Date.now() - startTime;
          const progress = Math.min(elapsed / duration, 1);

          mesh.material.emissive.setRGB(
            startColor.r + (targetColor.r - startColor.r) * progress,
            startColor.g + (targetColor.g - startColor.g) * progress,
            startColor.b + (targetColor.b - startColor.b) * progress,
          );

          if (progress < 1) {
            animations.set(mesh.id, requestAnimationFrame(animateHighlight));
          } else {
            mesh.material.emissive.copy(targetColor);
            animations.delete(mesh.id);
          }
        };

        animations.set(mesh.id, requestAnimationFrame(animateHighlight));
      });
    });

    return () => {
      animations.forEach((id) => cancelAnimationFrame(id));
    };
  }, [hoveredArtwork, artworks, viewingArtwork, isLookingAround]);

  // Animacja przejścia kamery (dojazd do obrazu / powrót na start)
  useFrame(() => {
    if (isTransitioning && targetPosition.current && targetQuaternion.current) {
      const elapsed = Date.now() - transitionStartTime.current;
      const progress = Math.min(elapsed / transitionDuration, 1);
      const easeProgress = easeInOutCubic(progress);

      camera.position.lerpVectors(
        transitionFromPosition.current,
        targetPosition.current,
        easeProgress,
      );
      camera.quaternion.slerpQuaternions(
        transitionFromQuaternion.current,
        targetQuaternion.current,
        easeProgress,
      );

      if (progress >= 1) {
        setIsTransitioning(false);
        camera.position.copy(targetPosition.current);
        camera.quaternion.copy(targetQuaternion.current);
      }
    }
  });

  // Wyjście z widoku obrazu: ESC / S / strzałka w dół / prawy przycisk myszy
  useEffect(() => {
    const handleKeyDown = (event) => {
      if (!viewingArtwork || isTransitioning) return;
      if (
        event.key === "Escape" ||
        event.key.toLowerCase() === "s" ||
        event.key === "ArrowDown"
      ) {
        exitArtwork();
      }
    };

    const handleRightClick = (e) => {
      if (viewingArtwork && e.button === 2 && !isTransitioning) {
        e.preventDefault();
        exitArtwork();
      }
    };

    const handleContextMenu = (e) => {
      if (viewingArtwork) e.preventDefault();
    };

    window.addEventListener("keydown", handleKeyDown);
    gl.domElement.addEventListener("mousedown", handleRightClick);
    gl.domElement.addEventListener("contextmenu", handleContextMenu);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      gl.domElement.removeEventListener("mousedown", handleRightClick);
      gl.domElement.removeEventListener("contextmenu", handleContextMenu);
    };
  }, [viewingArtwork, isTransitioning, gl, camera, exitArtwork]);

  // Przełączanie między obrazami klawiszami A/D lub strzałkami lewo/prawo
  useEffect(() => {
    if (!viewingArtwork || isTransitioning) return;

    const handleKeyNavigation = (event) => {
      let direction = null;
      if (event.key === "a" || event.key === "A" || event.key === "ArrowLeft") {
        direction = "prev";
      } else if (
        event.key === "d" ||
        event.key === "D" ||
        event.key === "ArrowRight"
      ) {
        direction = "next";
      }
      if (!direction) return;

      const currentIndex = artworks.findIndex(
        (artwork) => artwork.name === viewingArtwork.name,
      );
      if (currentIndex === -1) return;

      const newIndex =
        direction === "next"
          ? (currentIndex + 1) % artworks.length
          : (currentIndex - 1 + artworks.length) % artworks.length;

      flyToArtwork(artworks[newIndex]);
    };

    window.addEventListener("keydown", handleKeyNavigation);
    return () => window.removeEventListener("keydown", handleKeyNavigation);
  }, [artworks, viewingArtwork, isTransitioning, flyToArtwork]);

  return viewingArtwork ? (
    <Html center position={[0, -0.5, -1]} transform occlude>
      <div className="artwork-viewer-info">
        Wyjście: ESC, S, strzałka w dół lub prawy przycisk myszy | Przełączanie
        obrazów: A/D lub strzałki
      </div>
    </Html>
  ) : null;
}

// Sterowanie w pierwszej osobie - ruch WASD, rozglądanie się prawym przyciskiem myszy, teleport lewym
function FirstPersonControls({
  walls = [],
  viewingArtwork = null,
  setIsLookingAround,
}) {
  const { camera, gl, scene, raycaster, pointer } = useThree();
  const [lockControls, setLockControls] = useState(false);
  const [mouseDown, setMouseDown] = useState(false);
  const [teleporting, setTeleporting] = useState(false);
  const teleportTarget = useRef(null);
  const teleportStartTime = useRef(0);
  const teleportDuration = 1500; // ms

  // Zmniejsz prędkość poruszania się dla lepszej kontroli
  const moveSpeed = useRef(3.0);
  const keys = useRef({});
  const mouseSensitivity = useRef(0.002);
  const time = useRef(Date.now());
  const playerRadius = 0.3;
  const euler = useRef(new THREE.Euler(0, 0, 0, "YXZ"));
  const currentVelocity = useRef(new THREE.Vector3(0, 0, 0));
  const lastValidPosition = useRef(camera.position.clone());

  // Inicjalizacja pozycji kamery - wnętrze pokoju, na wysokości oczu, patrząc w stronę kominka i mebli
  useEffect(() => {
    camera.position.set(0, 1.6, 5);
    camera.rotation.set(0, 0, 0);
    euler.current.set(0, 0, 0);
    lastValidPosition.current.copy(camera.position);
  }, [camera]);

  // Zsynchronizuj euler (używany do rozglądania się myszą) z rzeczywistą
  // rotacją kamery po powrocie z widoku obrazu - inaczej pierwsze obrócenie
  // myszą "skoczyłoby" do starej, nieaktualnej wartości euler.
  useEffect(() => {
    if (!viewingArtwork) {
      euler.current.setFromQuaternion(camera.quaternion, "YXZ");
    }
  }, [viewingArtwork, camera]);

  // Obsługa klawiszy
  useEffect(() => {
    const handleKeyDown = (e) => {
      keys.current[e.key.toLowerCase()] = true;
    };

    const handleKeyUp = (e) => {
      keys.current[e.key.toLowerCase()] = false;
    };

    window.addEventListener("keydown", handleKeyDown);
    window.addEventListener("keyup", handleKeyUp);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, []);

  // Obsługa myszy i blokady kursora
  useEffect(() => {
    const canvas = gl.domElement;

    // Zapobiegaj standardowemu menu kontekstowemu przeglądarki
    const handleContextMenu = (e) => {
      e.preventDefault();
    };

    const handleMouseDown = (e) => {
      if (viewingArtwork) return; // Blokada kontroli podczas oglądania obrazu

      // Zmiana na prawy przycisk myszy (button === 2)
      if (e.button === 2) {
        setMouseDown(true);
        if (setIsLookingAround) setIsLookingAround(true);

        try {
          // W nowszych przeglądarkach używamy nowych opcji API PointerLock
          if (canvas.requestPointerLock) {
            // Sprawdź czy przeglądarka obsługuje suppressBanner (opcja niestandardowa Chrome)
            if (
              typeof Element.prototype.requestPointerLock === "function" &&
              "pointerLockOptions" in Element.prototype.requestPointerLock
            ) {
              canvas.requestPointerLock({
                unadjustedMovement: true,
                suppressBanner: true, // Ta opcja ukrywa baner "Naciśnij ESC..."
              });
            } else {
              // Standardowe API bez dodatkowych opcji
              canvas.requestPointerLock();
            }
          }
        } catch (error) {
          console.log("Błąd podczas próby zablokowania kursora:", error);
          canvas.requestPointerLock(); // Fallback do standardowego API
        }
      }
    };

    const handleMouseUp = (e) => {
      // Zmiana na prawy przycisk myszy (button === 2)
      if (e.button === 2) {
        setMouseDown(false);
        if (setIsLookingAround) setIsLookingAround(false);
        if (document.pointerLockElement === canvas) {
          document.exitPointerLock();
        }
      }
    };

    const handlePointerLockChange = () => {
      if (document.pointerLockElement === canvas) {
        setLockControls(true);
        document.body.style.cursor = "none";

        // Pokaż własny komunikat zamiast systemowego
        const customMessage = document.getElementById(
          "custom-pointer-lock-message",
        );
        if (customMessage) {
          customMessage.style.display = "block";
        }

        // Dodaj element, który pomoże ukryć komunikat systemowy
        if (!document.querySelector(".pointer-lock-hide-message")) {
          const hideElement = document.createElement("div");
          hideElement.className = "pointer-lock-hide-message";
          document.body.appendChild(hideElement);
        }
      } else {
        setLockControls(false);
        document.body.style.cursor = "auto";
        setMouseDown(false);

        // Ukryj własny komunikat
        const customMessage = document.getElementById(
          "custom-pointer-lock-message",
        );
        if (customMessage) {
          customMessage.style.display = "none";
        }

        // Usuń element ukrywający komunikat systemowy
        const hideElement = document.querySelector(
          ".pointer-lock-hide-message",
        );
        if (hideElement) {
          hideElement.remove();
        }
      }
    };

    const handleMouseMove = (e) => {
      if (viewingArtwork) return; // Blokada kontroli podczas oglądania obrazu
      if (!lockControls || !mouseDown) return;

      const movementX = e.movementX || e.mozMovementX || 0;
      const movementY = e.movementY || e.mozMovementY || 0;

      euler.current.y -= movementX * mouseSensitivity.current;
      euler.current.x -= movementY * mouseSensitivity.current;
      euler.current.x = Math.max(
        -Math.PI / 2 + 0.1,
        Math.min(Math.PI / 2 - 0.1, euler.current.x),
      );
      camera.quaternion.setFromEuler(euler.current);
    };

    // Dodaj nowy event listener dla contextmenu
    canvas.addEventListener("contextmenu", handleContextMenu);
    canvas.addEventListener("mousedown", handleMouseDown);
    canvas.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("mouseup", handleMouseUp);
    document.addEventListener("pointerlockchange", handlePointerLockChange);
    document.addEventListener("mousemove", handleMouseMove);

    return () => {
      // Usuń listener dla contextmenu
      canvas.removeEventListener("contextmenu", handleContextMenu);
      canvas.removeEventListener("mousedown", handleMouseDown);
      canvas.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener("mouseup", handleMouseUp);
      document.removeEventListener(
        "pointerlockchange",
        handlePointerLockChange,
      );
      document.removeEventListener("mousemove", handleMouseMove);
    };
  }, [gl, lockControls, mouseDown, camera, viewingArtwork, setIsLookingAround]);

  // Dodaj obsługę kliknięcia lewym przyciskiem myszy dla teleportacji
  useEffect(() => {
    const handleLeftClick = (e) => {
      if (viewingArtwork || e.button !== 0 || teleporting) return;
      raycaster.setFromCamera(pointer, camera);
      const floorObjects = [];
      scene.traverse((object) => {
        if (
          object.isMesh &&
          (object.name.toLowerCase().includes("floor") ||
            object.name.toLowerCase().includes("ground"))
        ) {
          floorObjects.push(object);
        }
      });

      // Sprawdź przecięcie z podłogą
      const intersects = raycaster.intersectObjects(floorObjects);

      if (intersects.length > 0) {
        const point = intersects[0].point;
        const currentHeight = camera.position.y;
        teleportTarget.current = {
          position: new THREE.Vector3(point.x, currentHeight, point.z),
        };
        setTeleporting(true);
        teleportStartTime.current = Date.now();
      }
    };

    gl.domElement.addEventListener("mousedown", handleLeftClick);

    return () => {
      gl.domElement.removeEventListener("mousedown", handleLeftClick);
    };
  }, [raycaster, pointer, camera, gl, scene, teleporting, viewingArtwork]);

  // Logika ruchu - wyłączona podczas oglądania obrazu
  useFrame(() => {
    if (viewingArtwork) return;

    const currentTime = Date.now();
    const delta = Math.min((currentTime - time.current) / 1000, 0.1);
    time.current = currentTime;

    // Kierunek ruchu na podstawie wciśniętych klawiszy
    const targetVelocity = new THREE.Vector3(0, 0, 0);
    const forward = keys.current["w"] || keys.current["arrowup"] || false;
    const backward = keys.current["s"] || keys.current["arrowdown"] || false;
    const left = keys.current["a"] || keys.current["arrowleft"] || false;
    const right = keys.current["d"] || keys.current["arrowright"] || false;

    if (forward) targetVelocity.z -= 1;
    if (backward) targetVelocity.z += 1;
    if (left) targetVelocity.x -= 1;
    if (right) targetVelocity.x += 1;

    if (targetVelocity.length() > 0) targetVelocity.normalize();

    const worldForward = new THREE.Vector3(0, 0, -1).applyQuaternion(
      camera.quaternion,
    );
    worldForward.y = 0;
    worldForward.normalize();

    const worldRight = new THREE.Vector3(1, 0, 0).applyQuaternion(
      camera.quaternion,
    );
    worldRight.y = 0;
    worldRight.normalize();

    if (targetVelocity.length() > 0) {
      currentVelocity.current.set(0, 0, 0);

      if (targetVelocity.z !== 0) {
        currentVelocity.current.add(
          worldForward
            .clone()
            .multiplyScalar(-targetVelocity.z * moveSpeed.current),
        );
      }
      if (targetVelocity.x !== 0) {
        currentVelocity.current.add(
          worldRight
            .clone()
            .multiplyScalar(targetVelocity.x * moveSpeed.current),
        );
      }
    } else {
      currentVelocity.current.set(0, 0, 0);
    }

    // Zastosuj ruch z obsługą kolizji
    if (currentVelocity.current.length() > 0) {
      const moveVector = currentVelocity.current.clone().multiplyScalar(delta);
      const targetPosition = camera.position.clone().add(moveVector);

      // Sprawdź kolizje
      if (!checkCollision(camera.position, targetPosition, walls, [], playerRadius)) {
        camera.position.copy(targetPosition);
        lastValidPosition.current.copy(camera.position);
      } else {
        // W przypadku kolizji, spróbuj przesunąć się tylko w kierunku X lub Z
        const xMove = new THREE.Vector3(moveVector.x, 0, 0);
        const zMove = new THREE.Vector3(0, 0, moveVector.z);

        let canMoveX = false;
        let canMoveZ = false;

        if (xMove.length() > 0) {
          const xTarget = camera.position.clone().add(xMove);
          canMoveX = !checkCollision(camera.position, xTarget, walls, [], playerRadius);
        }

        if (zMove.length() > 0) {
          const zTarget = camera.position.clone().add(zMove);
          canMoveZ = !checkCollision(camera.position, zTarget, walls, [], playerRadius);
        }

        if (canMoveX) {
          camera.position.x += xMove.x;
        }
        if (canMoveZ) {
          camera.position.z += zMove.z;
        }

        if (canMoveX || canMoveZ) {
          lastValidPosition.current.copy(camera.position);
        } else {
          // Jeśli nie można się poruszyć w żadnym kierunku, wróć do ostatniej ważnej pozycji
          camera.position.copy(lastValidPosition.current);
        }

        currentVelocity.current.set(0, 0, 0);
      }
    }

    // Obsługa teleportacji
    if (teleporting && teleportTarget.current) {
      const now = Date.now();
      const elapsed = now - teleportStartTime.current;
      const progress = Math.min(elapsed / teleportDuration, 1);

      // Użyj krzywej wygładzania - szybsze w środku, bardziej naturalne na początku i końcu
      const easeProgress =
        progress < 0.5
          ? 2 * progress * progress
          : 1 - Math.pow(-2 * progress + 2, 2) / 2;

      // Interpoluj pozycję
      const startPos = camera.position.clone();
      const targetPos = teleportTarget.current.position;

      // Prosta interpolacja liniowa z zachowaniem wysokości
      const newPos = new THREE.Vector3(
        startPos.x + (targetPos.x - startPos.x) * easeProgress,
        startPos.y, // Zachowaj stałą wysokość Y
        startPos.z + (targetPos.z - startPos.z) * easeProgress,
      );

      // Ustaw nową pozycję
      camera.position.copy(newPos);

      // Odblokuj kontrolę już przy 70% animacji
      if (progress >= 0.7) {
        setTeleporting(false);

        if (progress < 1) {
          // Ustaw końcową pozycję jeśli to ostatni krok
          if (progress > 0.95) {
            camera.position.set(targetPos.x, startPos.y, targetPos.z);
          }
        }
      }
    }
  });

  return null;
}

// Funkcja sprawdzająca kolizję między punktami i ścianami
function checkCollision(start, end, walls, doors = [], radius = 0.3) {
  // Tablice obiektów kolizyjnych
  const wallMeshes = walls.filter((w) => w.mesh).map((w) => w.mesh);
  const doorMeshes = doors.filter((d) => d.mesh).map((d) => d.mesh);
  const allColliders = [...wallMeshes, ...doorMeshes];

  // Jeśli nie ma obiektów kolizyjnych, loguj błąd
  if (allColliders.length === 0) {
    console.warn("checkCollision: Brak obiektów kolizyjnych!");
    return false;
  }

  // Kierunek i odległość ruchu
  const moveVec = new THREE.Vector3().subVectors(end, start);
  const direction = moveVec.clone().normalize();
  const distance = moveVec.length();

  // Podstawowy promień sprawdzania kolizji
  const raycaster = new THREE.Raycaster(start, direction, 0, distance + radius);
  const hits = raycaster.intersectObjects(allColliders, true);

  if (hits.length > 0) {
    return true;
  }

  // Dodatkowe promienie dla lepszej detekcji kolizji
  const sideAngles = [
    Math.PI / 4,
    Math.PI / 2,
    (3 * Math.PI) / 4,
    Math.PI,
    (5 * Math.PI) / 4,
    (3 * Math.PI) / 2,
    (7 * Math.PI) / 4,
  ];

  for (const angle of sideAngles) {
    const x = Math.cos(angle) * radius;
    const z = Math.sin(angle) * radius;
    const offsetStart = start.clone().add(new THREE.Vector3(x, 0, z));

    const sideRaycaster = new THREE.Raycaster(
      offsetStart,
      direction,
      0,
      distance,
    );
    const sideHits = sideRaycaster.intersectObjects(allColliders, true);

    if (sideHits.length > 0 && sideHits[0].distance <= distance) {
      return true;
    }
  }

  return false;
}

// HDRI obrócone o 160 stopni (patrz useRotatedHDRI.js - obrót "w osi Z")
function HDRIEnvironment() {
  const texture = useRotatedHDRI(HDRI_URL, 160);
  return <Environment map={texture} />;
}

// Główny komponent renderera
const DesktopRenderer = () => {
  const [walls, setWalls] = useState([]);
  const [artworks, setArtworks] = useState([]);
  const [viewingArtwork, setViewingArtwork] = useState(null);
  const [isLookingAround, setIsLookingAround] = useState(false);

  // Ukryj overlay ładowania po pewnym czasie
  useEffect(() => {
    const timer = setTimeout(() => {
      const loadingOverlay = document.getElementById("loading-overlay");
      if (loadingOverlay) {
        loadingOverlay.style.opacity = 0;
        setTimeout(() => {
          loadingOverlay.style.display = "none";
        }, 500);
      }
    }, 3000);

    return () => clearTimeout(timer);
  }, []);

  // Funkcja wywołana po utworzeniu canvasa
  const handleCreated = () => {
    setTimeout(() => {
      const loadingOverlay = document.getElementById("loading-overlay");
      if (loadingOverlay) {
        loadingOverlay.style.opacity = 0;
        setTimeout(() => {
          loadingOverlay.style.display = "none";
        }, 500);
      }
    }, 1000);
  };

  return (
    <div className="renderer-container">
      <Canvas
        dpr={[1, 2]}
        camera={{
          position: [0, 1.65, 5],
          rotation: [0, 0, 0],
          fov: 40,
        }}
        style={{ width: "100%", height: "100%" }}
        onCreated={handleCreated}
      >
        <color attach="background" args={["#cfc9c0"]} />
        {/* Model nie zawiera własnych świateł, więc oświetlenie sceny ustawiamy ręcznie */}
        <ambientLight intensity={0.8} color="#fff6e6" />

        <hemisphereLight
          skyColor="#fff0dd"
          groundColor="#e0e0ff"
          intensity={0.6}
          position={[0, 50, 0]}
        />

        <directionalLight
          position={[3, 4.5, -3]}
          intensity={1.2}
          color="#fff2df"
          castShadow
          shadow-mapSize-width={1024}
          shadow-mapSize-height={1024}
          shadow-camera-near={0.5}
          shadow-camera-far={20}
          shadow-camera-left={-6}
          shadow-camera-right={6}
          shadow-camera-top={6}
          shadow-camera-bottom={-6}
        />

        {/* Światło uzupełniające z przeciwnej strony, by druga strona pokoju nie była czarna */}
        <directionalLight position={[-3, 3, 4]} intensity={0.7} color="#e7ecff" />

        <Suspense fallback={<LoadingIndicator />}>
          <HDRIEnvironment />
          <Model setWalls={setWalls} setArtworks={setArtworks} />
          <FirstPersonControls
            walls={walls}
            viewingArtwork={viewingArtwork}
            setIsLookingAround={setIsLookingAround}
          />
          {artworks.length > 0 && (
            <ArtworkInteraction
              artworks={artworks}
              setViewingArtwork={setViewingArtwork}
              viewingArtwork={viewingArtwork}
              isLookingAround={isLookingAround}
            />
          )}
        </Suspense>
      </Canvas>

      <div id="loading-overlay" className="loading-overlay"></div>

      {/* Panel z opisem sterowania */}
      <div className="interface-container">
        {viewingArtwork ? (
          <div className="controls-info">
            Wyjście: ESC, S, strzałka w dół lub prawy przycisk myszy |
            Przełączanie obrazów: A/D lub strzałki
          </div>
        ) : (
          <div className="controls-info">
            WASD - poruszanie | Prawy przycisk - rozglądanie | Lewy przycisk -
            teleport | Kliknij na obraz, aby zobaczyć
          </div>
        )}
      </div>
    </div>
  );
};

export default DesktopRenderer;
